import { useCallback, useEffect, useMemo, useState } from "react";

import type { DataNode } from "antd/es/tree";
import { useIntl } from "react-intl";

import TerritoryLevel from "@mapmycustomers/shared/enum/TerritoryLevel";
import AreaRegion, { AreaMetaFile } from "@mapmycustomers/shared/types/territory/AreaRegion";
import AreaType from "@mapmycustomers/shared/types/territory/AreaType";
import type LookupData from "@mapmycustomers/shared/types/territory/LookupData";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import { parseAreaType } from "@mapmycustomers/shared/util/territory/areaTypes";

import getBoundaryUrl from "@app/scene/map/utils/getBoundaryUrl";

const titleComparator = (a: DataNode, b: DataNode) =>
  ((a.title as string) ?? "").localeCompare((b.title as string) ?? "");

const getNextLevel = (level: TerritoryLevel) => {
  switch (level) {
    case TerritoryLevel.ADM1:
      return TerritoryLevel.ADM2;
    case TerritoryLevel.ADM2:
      return TerritoryLevel.ADM3;
    default:
      return undefined;
  }
};

type SelectionState = "all" | "none" | "some";

const getParentSelectionState = (childrenSelectionStates: SelectionState[]): SelectionState => {
  if (!childrenSelectionStates.length) {
    return "all"; // if no children, we can say that "all" is selected
  }

  let selectionState = childrenSelectionStates[0];
  for (let i = 1; i < childrenSelectionStates.length; i++) {
    switch (childrenSelectionStates[i]) {
      case "all":
        // if previous was "all" and we continue getting "all", then we keep it
        // otherwise, we switch to "some" if it was not "all" before
        selectionState = selectionState === "all" ? selectionState : "some";
        break;
      case "some":
        // well... if we face "some", we proceed with "some"
        selectionState = "some";
        break;
      case "none":
        // if previous was all "all", then we go with "some"
        // otherwise, we keep previous state (it can only be "none" in this case
        // because for "some" we'd break the loop, see below)
        selectionState = selectionState === "all" ? "some" : selectionState;
        break;
    }
    if (selectionState === "some") {
      break; // no need to proceed, it can't become "all" or "none" anymore
    }
  }

  return selectionState;
};

const useTreeData = (
  baseAreaType: AreaType,
  lookupData: LookupData,
  query: string,
  selectedRegionIds?: AreaRegion["id"][],
  onSelectionChange?: (selectedItems: AreaRegion["id"][]) => void
): [
  treeData: DataNode[],
  expandedKeys: string[],
  loading: boolean,
  selectedTreeKeys: { checked: string[]; halfChecked: string[] },
  handleTreeCheck: (_: any, info: { checked: boolean; node: DataNode }) => void,
  handleExpand: (expandedKeys: Array<number | string>) => void
] => {
  const intl = useIntl();

  const { countryCode, level: baseLevel } = parseAreaType(baseAreaType)!;

  const [metaLoading, setMetaLoading] = useState<boolean>(false);
  const [regionNames, setRegionNames] = useState<Map<string, AreaRegion["name"]>>(new Map());

  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

  useEffect(() => {
    const levelsToDisplay = Object.values(TerritoryLevel).filter((level) => level <= baseLevel);
    setMetaLoading(true);
    Promise.all(
      levelsToDisplay.map((level) =>
        fetch(getBoundaryUrl("meta", `${countryCode}_${level}` as AreaType)).then((response) =>
          response.json()
        )
      )
    ).then((metaDatas: AreaMetaFile[]) => {
      const map = new Map<string, AreaRegion["name"]>();
      levelsToDisplay.forEach((level, index) => {
        const meta = metaDatas[index];
        meta.regions.forEach(({ id, name }) => {
          const key = `${level}_${id}`;
          map.set(key, name);
        });
      });
      setRegionNames(map);
      setMetaLoading(false);
    });
  }, [countryCode, baseLevel]);

  const [treeData, matchedKeys, checkedTreeKeys] = useMemo(() => {
    const selectedRegionIdsSet = new Set(selectedRegionIds ?? []);
    const matchedKeys = new Set<string>();
    const checkedKeys = new Set<string>();
    const halfCheckedKeys = new Set<string>();

    const getNodesForLevelAndRegion = (
      baseLevel: TerritoryLevel,
      level: TerritoryLevel,
      q: string,
      parentRegionId?: string,
      keyPrefix: string = ""
    ): [dataNodes: DataNode[], checked: "all" | "none" | "some"] => {
      const nextLevel = getNextLevel(level);
      const isLeaf = level === baseLevel || !nextLevel;
      let regionIds: string[] = [];

      if (level === TerritoryLevel.ADM1) {
        regionIds = Object.keys(lookupData[level]);
      } else if (level === TerritoryLevel.ADM2 && parentRegionId) {
        regionIds = lookupData[TerritoryLevel.ADM1][parentRegionId][TerritoryLevel.ADM2];
      } else if (level === TerritoryLevel.ADM3 && parentRegionId) {
        regionIds = lookupData[TerritoryLevel.ADM2][parentRegionId][TerritoryLevel.ADM3];
      }

      const childrenSelectionStates: SelectionState[] = [];

      const dataNodes: DataNode[] = regionIds
        .filter(
          (regionId) => regionNames.has(`${level}_${regionId}`) || level === TerritoryLevel.ADM3
        )
        .map<DataNode | undefined>((regionId) => {
          // Some regions are shared between different parent regions. E.g. a post code
          // can be located in two different counties.
          // That leads to nodes in the tree with the same keys. And this is absolutely prohibited.
          // So we're making keys unique, but adding a path/prefix to them.
          const uniqueKey = `${keyPrefix}_${level}_${regionId}`;
          let title = regionNames.get(`${level}_${regionId}`);
          if (!title) {
            if (level === TerritoryLevel.ADM3) {
              title = intl.formatMessage(
                {
                  id: "map.territories.sidebar.areaRegionTree.poBox",
                  defaultMessage: `{regionId} (P.O. box)`,
                  description:
                    "Region title shown when there's no name, typically used for zip codes which are PO Boxes",
                },
                { regionId }
              );
            } else {
              title = regionId;
            }
          }

          const titleMatches = title.toLowerCase().includes(q);
          if (titleMatches) {
            matchedKeys.add(uniqueKey);
          }

          const isNodeSelected = isLeaf && selectedRegionIdsSet.has(regionId);
          const [children, selectionState = isNodeSelected ? "all" : "none"] =
            q && titleMatches && !isLeaf
              ? // if current node matched, then try getting children again, but like
                // there was no query, to return them all
                getNodesForLevelAndRegion(baseLevel, nextLevel!, "", regionId, uniqueKey)
              : isLeaf
              ? [undefined, undefined]
              : getNodesForLevelAndRegion(baseLevel, nextLevel, q, regionId, uniqueKey);

          if (selectionState === "all") {
            checkedKeys.add(uniqueKey);
          } else if (selectionState === "some") {
            halfCheckedKeys.add(uniqueKey);
          }

          childrenSelectionStates.push(selectionState);

          // if we do filter, but none of children match and this node doesn't
          // match too, then no need to return it
          return q && !children?.length && !titleMatches
            ? undefined
            : { children, isLeaf, key: uniqueKey, title };
        })
        .filter(isDefined)
        .sort(titleComparator);

      return [dataNodes, getParentSelectionState(childrenSelectionStates)];
    };

    const [dataNodes] = getNodesForLevelAndRegion(
      baseLevel,
      TerritoryLevel.ADM1,
      query.toLowerCase()
    );

    return [
      dataNodes,
      matchedKeys,
      { checked: Array.from(checkedKeys), halfChecked: Array.from(halfCheckedKeys) },
    ];
  }, [baseLevel, lookupData, regionNames, query, intl, selectedRegionIds]);

  const handleTreeCheck = useDynamicCallback(
    (_: any, info: { checked: boolean; node: DataNode }) => {
      // every key always has its own level and regionId at the end
      const [level, regionId] = (info.node.key as string).split("_").slice(-2) as [
        TerritoryLevel,
        AreaRegion["id"]
      ];
      let affectedRegionIds: AreaRegion["id"][] = [];
      if (level === baseLevel || level === TerritoryLevel.ADM3) {
        affectedRegionIds.push(regionId);
      } else if (level === TerritoryLevel.ADM1 || level === TerritoryLevel.ADM2) {
        // TS has concerns about baseLevel in this query, but we know that the only
        // possible levels are ADM3 for level=ADM1 or level=ADM2
        // or ADM2 for level=ADM1. So it's a safe query.
        // @ts-ignore
        affectedRegionIds = lookupData[level][regionId]?.[baseLevel] ?? [];
      }

      if (info.checked) {
        const resultingIds = Array.from(
          new Set([...(selectedRegionIds ?? []), ...affectedRegionIds])
        );
        onSelectionChange?.(resultingIds);
      } else {
        const affectedRegionIdsSet = new Set(affectedRegionIds);
        const resultingIds = (selectedRegionIds ?? []).filter(
          (regionId) => !affectedRegionIdsSet.has(regionId)
        );
        onSelectionChange?.(resultingIds);
      }
    }
  );

  useEffect(() => {
    if (query.length < 3) {
      return; // do nothing until query is 3 chars or longer
    }

    // We not only add matched keys themselves into expanded, but also all their parents
    // So if we had a key "_A_0_B_1_C_2", then `match` would return ["_A_0", "_B_1", "_C_2"],
    // and `reduce` will convert that into ["_A_0", "_A_0_B_1", "_A_0_B_1_C_2"]
    // And we put all these keys into expandedKeys
    setExpandedKeys(
      Array.from(matchedKeys).flatMap((key) =>
        (key.match(/_[^_]+_[^_]+/g) || []).reduce(
          (result, item) => (!result.length ? [item] : [...result, `${result.at(-1)}${item}`]),
          [] as string[]
        )
      )
    );
  }, [query, matchedKeys]);

  const handleExpand = useCallback(
    (expandedKeys: Array<number | string>) => setExpandedKeys(expandedKeys as string[]), // we know all our keys are string keys
    []
  );

  return [treeData, expandedKeys, metaLoading, checkedTreeKeys, handleTreeCheck, handleExpand];
};

export default useTreeData;
